import {
    AI_CREATE_SESSION_STATUS,
    AI_CREATE_VARIANT_STATUS,
    type GetAICreateSessionResponse,
} from '@storefronts/common-types'
import type { AiCreateRunSignals, StreamGate } from './AiCreateRunSignals'

/** What a snapshot can still say after the AG-UI run log was deleted. */
export type SnapshotRecovery = 'fail' | 'complete'

/**
 * Terminal AG-UI events are not replayed: the run log is dropped on
 * RUN_FINISHED / RUN_ERROR. A refresh therefore has to recover from the
 * snapshot. In-progress session statuses are not a gate.
 */
export function snapshotRecovery(
    response: GetAICreateSessionResponse,
): SnapshotRecovery | null {
    if (response.status === AI_CREATE_SESSION_STATUS.FAILED) {
        return 'fail'
    }
    const primary =
        response.variants?.find((variant) => variant.isPrimary) ??
        response.variants?.[0]
    if (
        primary?.status === AI_CREATE_VARIANT_STATUS.DONE ||
        primary?.status === AI_CREATE_VARIANT_STATUS.FAILED
    ) {
        return 'complete'
    }
    return null
}

/**
 * The safety clock starts with the session subscription, including before
 * RUN_STARTED. A queued job or a stream that never opens must still time out.
 * It stays off once the run has failed, the primary pipeline has finished, or
 * the snapshot is already terminal.
 */
export function shouldArmRunTimeout(
    signals: Pick<AiCreateRunSignals, 'runFailed' | 'primaryPipelineFinished'>,
    snapshot: GetAICreateSessionResponse | null,
): boolean {
    if (signals.runFailed || signals.primaryPipelineFinished) {
        return false
    }
    if (!snapshot) {
        return true
    }
    return snapshotRecovery(snapshot) === null
}

/**
 * Recover from the stored snapshot only when RUN_FINISHED ends a run the
 * stream did not already settle. A stream reset wipes `hasActivity`; a
 * finish after `primaryPipelineFinished` or `runFailed` must not replay a
 * stale snapshot over that outcome.
 */
export function shouldRecoverStoredSnapshot(
    prev: Pick<
        AiCreateRunSignals,
        'runActive' | 'primaryPipelineFinished' | 'runFailed'
    >,
    next: Pick<AiCreateRunSignals, 'runActive' | 'hasActivity'>,
    gates: StreamGate[],
): boolean {
    if (!prev.runActive || next.runActive || gates.length > 0) {
        return false
    }
    if (!next.hasActivity) {
        return false
    }
    return !prev.primaryPipelineFinished && !prev.runFailed
}
